import { v4 as uuidv4 } from "uuid";
import { DocumentUploadStrategy } from "./DocumentUploadStrategy";
import { UserContext } from "../utils/UserContext";
import { DocumentVersionManager } from "../managers/DocumentVersionManager";

export class UnlinkedDocumentUploadStrategy implements DocumentUploadStrategy {
  constructor(
    private client: any,
    private documentVersionManager: DocumentVersionManager,
  ) {}

  async execute(
    userContext: UserContext,
    s3Key: string,
    fileName: string,
    documentType: string,
    contentHash: string,
  ): Promise<void> {
    try {
      const { data: allDocs, errors } = await this.client.models.Document.list({
        filter: {
          userId: { eq: userContext.identityId },
        },
        authMode: "userPool",
        authToken: userContext.token,
      });

      if (errors) {
        console.error("[Error listing documents:", errors);
        throw new Error(`Failed to list documents: ${JSON.stringify(errors)}`);
      }

      const existingDocs = (allDocs || []).filter(
        (doc: any) =>
          doc.documentType === documentType &&
          doc.status === "ACTIVE" &&
          doc.isLatestVersion === true,
      );

      const documentData = this.buildDocumentData(
        userContext,
        s3Key,
        fileName,
        documentType,
        contentHash,
      );

      if (existingDocs.length > 0) {
        const existingDoc = existingDocs[0];

        await this.documentVersionManager.createNewVersion(
          existingDoc.documentId,
          documentData,
          userContext,
        );
      } else {
        await this.documentVersionManager.createInitialVersion(
          documentData,
          userContext,
        );
      }
    } catch (error) {
      console.error("Execution failed:", error);
      throw error;
    }
  }

  private buildDocumentData(
    userContext: UserContext,
    s3Key: string,
    fileName: string,
    documentType: string,
    contentHash: string,
  ) {
    const documentId = uuidv4();

    return {
      documentId,
      userId: userContext.identityId,
      ownerType: userContext.userType,
      documentType,
      fileKey: s3Key,
      fileName,
      contentHash,
      status: "ACTIVE",
      uploadedAt: new Date().toISOString(),
    };
  }
}
